/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import {
  createSlice,
  createEntityAdapter,
  createAction,
} from '@reduxjs/toolkit';
import type {
  PayloadAction,
  Draft,
  Reducer,
  UnknownAction,
} from '@reduxjs/toolkit';
import { produce } from 'immer';
import { compact } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import type { SystemScope, TimeSeriesService } from '@opengeoweb/shared';
import { mapUtils } from '@opengeoweb/store';
import type {
  ServiceFilterChipsObject,
  SetCurrentParameterInfo,
  SetSearchFilterPayload,
  MovePlotPayload,
  SetTimeSeriesServicesPayload,
  TimeSeriesState,
  TimeSeriesStoreType,
  ParameterManipulationPayload,
  UpdateTitlePayload,
  MoveParameterPayload,
  SetTimeSeriesServicePopupPayload,
  UpsertTimeSeriesServicePayload,
  RemoveServiceFilterChipFromStorePayload,
  RemoveTimeSeriesServicePayload,
  ParameterActionPayload,
  PlotActionPayload,
  AddPlotPayload,
  DuplicatePlotPayload,
  ParameterPatchPayload,
  ToggleDisableMapPinPayload,
  ToggleLocationMarkersPayload,
  ToggleTimeSeriesLegendPayload,
  SetTimeSeriesLocationPayload,
  RegisterTimeSeriesPresetPayload,
  SetLocationMenuSizeAndPositionPayload,
  SetLocationsDialogOpenPayload,
  SetPlotSizePayload,
  SetPlotIsOpenPayload,
  ReorderPlotsPayload,
} from './types';
import type { Parameter } from '../components/TimeSeries/types';
import { getServiceById } from '../utils/edrUtils';

const patchableParameterProperties: Partial<Record<keyof Parameter, true>> = {
  color: true,
  plotType: true,
  opacity: true,
  instanceId: true,
  propertyName: true,
  dimensions: true,
};
const removableParameterPropertiesOnPatch: Partial<
  Record<keyof Parameter, true>
> = {
  color: true,
  opacity: true,
  instanceId: true,
  dimensions: true,
};

// Upserts each incoming {id, value} entry by id, preserving other stored dimensions
const mergeDimensions = (
  existingDimensions: { id: string; value: string }[] | undefined,
  incomingDimensions: { id: string; value: string }[],
): { id: string; value: string }[] => {
  const merged = [...(existingDimensions ?? [])];
  incomingDimensions.forEach((incoming) => {
    const existingIndex = merged.findIndex((dim) => dim.id === incoming.id);
    if (existingIndex >= 0) {
      merged[existingIndex] = incoming;
    } else {
      merged.push(incoming);
    }
  });
  return merged;
};

// Reads a preset parameter's dimensions, falling back to the pre-array `dimension` shape
const getNormalizedDimensions = (
  parameter: Parameter,
): { id: string; value: string }[] | undefined => {
  const legacyDimension = (
    parameter as Parameter & {
      dimension?: { id: string; value: string };
    }
  ).dimension;
  return (
    parameter.dimensions ?? (legacyDimension ? [legacyDimension] : undefined)
  );
};

export const timeseriesSelectServiceFilterChipsAdapter = createEntityAdapter<
  ServiceFilterChipsObject,
  string
>({
  selectId: (service) => service.serviceId!,
});

export const initialState: TimeSeriesState = {
  byId: {},
  services: [],
};

export const createPanelState = (): TimeSeriesStoreType => ({
  plotPreset: undefined,
  timeseriesSelect: {
    currentParameterInfo: undefined,
    filters: {
      searchFilter: '',
      serviceFilterChips:
        timeseriesSelectServiceFilterChipsAdapter.getInitialState(),
      allServiceFilterChipsEnabled: true,
    },
    servicePopup: {
      isOpen: false,
      variant: 'add',
    },
  },
  locationData: {
    isMapPinDisabled: false,
    shouldHideLocationMarkers: false,
    isLegendVisible: true,
  },
});

export const initialPanelState: TimeSeriesStoreType = createPanelState();

const panelSlice = createSlice({
  initialState: createPanelState(),
  name: 'timeseriesPanel',
  reducers: {
    loadUserAddedTimeSeriesServices: (
      draft: Draft<TimeSeriesStoreType>,
      payloadAction: PayloadAction<
        SetTimeSeriesServicesPayload | TimeSeriesService[]
      >,
    ) => {
      const servicesPayload = Array.isArray(payloadAction.payload)
        ? payloadAction.payload
        : payloadAction.payload.timeSeriesServices;
      const existingIds = new Set(
        draft.timeseriesSelect?.filters.serviceFilterChips.ids ?? [],
      );
      const newServices = servicesPayload
        .map((service) => {
          const id =
            service.scope === 'system' ? service.id : generateServiceId();
          return {
            ...service,
            id,
            scope: service.scope ?? 'user',
          };
        })
        .filter((service) => !existingIds.has(service.id));

      if (newServices.length) {
        panelSlice.caseReducers.setServiceFilterChipsInStore(draft, {
          payload: newServices.map(
            (service): ServiceFilterChipsObject => ({
              serviceId: service.id,
              serviceName: service.name,
              serviceUrl: service.url,
              type: service.type,
              scope: (service.scope as SystemScope) || 'user',
            }),
          ),
          type: timeSeriesActions.setServiceFilterChipsInStore.type,
        });
      }
    },
    registerTimeSeriesPreset: (
      draft: Draft<TimeSeriesStoreType>,
      action: PayloadAction<RegisterTimeSeriesPresetPayload>,
    ) => {
      draft.plotPreset = action.payload.plotPreset;

      const newServicesAdded: ServiceFilterChipsObject[] = (
        action.payload.services ?? []
      ).map((service) => ({
        serviceId: service.id,
        serviceUrl: service.url,
        serviceName: service.name,
        type: service.type,
        scope: (service.scope as SystemScope) || 'system',
      }));

      panelSlice.caseReducers.setServiceFilterChipsInStore(draft, {
        payload: newServicesAdded,
        type: timeSeriesActions.setServiceFilterChipsInStore.type,
      });
    },
    deletePlot: (
      draft: Draft<TimeSeriesStoreType>,
      action: PayloadAction<PlotActionPayload>,
    ) => {
      draft.plotPreset!.plots = draft.plotPreset!.plots.filter((plot) => {
        return plot.plotId !== action.payload.plotId;
      });
      draft.plotPreset!.parameters = draft.plotPreset!.parameters.filter(
        (parameter) => {
          return parameter.plotId !== action.payload.plotId;
        },
      );
    },
    togglePlot: (
      draft: Draft<TimeSeriesStoreType>,
      action: PayloadAction<PlotActionPayload>,
    ) => {
      draft.plotPreset!.plots = draft.plotPreset!.plots.map((plot) => {
        if (plot.plotId === action.payload.plotId) {
          const plotIsEnabled = plot.enabled !== false;
          return { ...plot, enabled: !plotIsEnabled };
        }
        return plot;
      });
    },
    addPlot: (
      draft: Draft<TimeSeriesStoreType>,
      action: PayloadAction<AddPlotPayload>,
    ) => {
      draft.plotPreset!.plots.push({
        title: action.payload.title,
        plotId: generateTimeSeriesId(),
      });
    },
    setPlotIsOpen: (
      draft: Draft<TimeSeriesStoreType>,
      action: PayloadAction<SetPlotIsOpenPayload>,
    ) => {
      const plot = draft.plotPreset?.plots.find(
        (item) => item.plotId === action.payload.plotId,
      );
      if (plot) {
        plot.isOpen = action.payload.isOpen;
      }
    },
    setPlotSize: (
      draft: Draft<TimeSeriesStoreType>,
      action: PayloadAction<SetPlotSizePayload>,
    ) => {
      draft.plotPreset!.plots = draft.plotPreset!.plots.map((plot) => {
        if (plot.plotId === action.payload.plotId) {
          return { ...plot, customSize: action.payload.size };
        }
        return plot;
      });
    },
    duplicatePlot: (
      draft: Draft<TimeSeriesStoreType>,
      action: PayloadAction<DuplicatePlotPayload>,
    ) => {
      const preset = draft.plotPreset;
      const sourceIndex = preset
        ? preset.plots.findIndex(
            (plot) => plot.plotId === action.payload.plotId,
          )
        : -1;
      if (!preset || sourceIndex < 0) {
        return;
      }
      const source = preset.plots[sourceIndex];
      const newPlotId = generateTimeSeriesId();
      const duplicatedPlot = {
        ...source,
        title: action.payload.title,
        plotId: newPlotId,
      };
      const duplicatedParameters = preset.parameters
        .filter((parameter) => parameter.plotId === source.plotId)
        .map((parameter) => ({
          ...parameter,
          id: generateParameterId(),
          plotId: newPlotId,
          ...(parameter.dimensions
            ? {
                dimensions: parameter.dimensions.map((dimension) => ({
                  ...dimension,
                })),
              }
            : {}),
        }));
      preset.plots.splice(sourceIndex + 1, 0, duplicatedPlot);
      preset.parameters.push(...duplicatedParameters);
    },
    deleteParameter: (
      draft: Draft<TimeSeriesStoreType>,
      action: PayloadAction<ParameterActionPayload>,
    ) => {
      draft.plotPreset!.parameters = draft.plotPreset!.parameters.filter(
        (parameter) => {
          return parameter.id !== action.payload.parameterId;
        },
      );
    },
    addParameter: (
      draft: Draft<TimeSeriesStoreType>,
      action: PayloadAction<ParameterManipulationPayload>,
    ) => {
      draft.plotPreset!.parameters.push({
        ...action.payload.parameter,
        id: generateParameterId(),
      });
    },
    toggleParameter: (
      draft: Draft<TimeSeriesStoreType>,
      action: PayloadAction<ParameterActionPayload>,
    ) => {
      draft.plotPreset!.parameters = draft.plotPreset!.parameters.map(
        (parameter) => {
          if (parameter.id === action.payload.parameterId) {
            const parameterIsEnabled = parameter.enabled !== false;
            return { ...parameter, enabled: !parameterIsEnabled };
          }
          return parameter;
        },
      );
    },
    patchParameter: (
      draft: Draft<TimeSeriesStoreType>,
      action: PayloadAction<ParameterPatchPayload>,
    ) => {
      const payloadParameter = action.payload.parameter;
      draft.plotPreset!.parameters = draft.plotPreset!.parameters.map(
        (initialParameter): Parameter => {
          if (initialParameter.id === payloadParameter.id) {
            const patchedParameter: Parameter = { ...initialParameter };
            (Object.keys(payloadParameter) as (keyof Parameter)[]).forEach(
              (propertyName) => {
                if (
                  removableParameterPropertiesOnPatch[propertyName] &&
                  payloadParameter[propertyName] === undefined &&
                  propertyName in payloadParameter
                ) {
                  delete patchedParameter[propertyName];
                } else if (
                  propertyName === 'dimensions' &&
                  payloadParameter.dimensions !== undefined
                ) {
                  patchedParameter.dimensions = mergeDimensions(
                    patchedParameter.dimensions,
                    payloadParameter.dimensions,
                  );
                } else if (
                  patchableParameterProperties[propertyName] &&
                  payloadParameter[propertyName] !== undefined
                ) {
                  (patchedParameter as Record<keyof Parameter, unknown>)[
                    propertyName
                  ] = payloadParameter[propertyName];
                }
              },
            );
            return patchedParameter;
          }
          return initialParameter;
        },
      );
    },
    updateTitle: (
      draft: Draft<TimeSeriesStoreType>,
      action: PayloadAction<UpdateTitlePayload>,
    ) => {
      const plot = draft.plotPreset?.plots.find(
        (elem) => elem.plotId === action.payload.plotId,
      );
      if (plot) {
        plot.title = action.payload.title;
      }
    },
    setSearchFilter: (
      draft: Draft<TimeSeriesStoreType>,
      action: PayloadAction<SetSearchFilterPayload>,
    ) => {
      const { filterText } = action.payload;
      if (draft.timeseriesSelect) {
        draft.timeseriesSelect.filters.searchFilter = filterText;
      }
    },
    movePlot: (
      draft: Draft<TimeSeriesStoreType>,
      action: PayloadAction<MovePlotPayload>,
    ) => {
      const { oldIndex, newIndex } = action.payload;
      if (!draft.plotPreset?.plots) {
        return;
      }
      draft.plotPreset.plots = mapUtils.moveArrayElements(
        draft.plotPreset.plots,
        oldIndex,
        newIndex,
      );
    },
    reorderPlots: (
      draft: Draft<TimeSeriesStoreType>,
      action: PayloadAction<ReorderPlotsPayload>,
    ) => {
      const { newOrder } = action.payload;
      if (!draft.plotPreset?.plots) {
        return;
      }
      const plotsById = new Map(
        draft.plotPreset.plots.map((plot) => [plot.plotId, plot]),
      );

      const orderedPlots = Array.from(new Set(newOrder))
        .map((id) => plotsById.get(id))
        .filter((plot) => plot !== undefined);
      const reorderedIds = new Set(orderedPlots.map((plot) => plot.plotId));
      let nextIndex = 0;
      // Hidden plots are absent from the sortable list and retain their position.
      draft.plotPreset.plots = draft.plotPreset.plots.map((plot) => {
        if (!reorderedIds.has(plot.plotId)) {
          return plot;
        }
        const reorderedPlot = orderedPlots[nextIndex];
        nextIndex += 1;
        return reorderedPlot;
      });
    },

    moveParameter: (
      draft: Draft<TimeSeriesStoreType>,
      action: PayloadAction<MoveParameterPayload>,
    ) => {
      const { oldIndex, newIndex, toPlotId, plotId } = action.payload;
      if (!draft.plotPreset) {
        return;
      }
      const oldPos =
        draft.plotPreset.parameters.findIndex(
          (param) => param.plotId === plotId,
        ) + oldIndex;
      const newPos =
        draft.plotPreset.parameters.findIndex(
          (param) => param.plotId === toPlotId,
        ) + newIndex;
      if (toPlotId !== plotId) {
        draft.plotPreset.parameters[oldPos].plotId = toPlotId;
      }
      draft.plotPreset.parameters = mapUtils.moveArrayElements(
        draft.plotPreset.parameters,
        oldPos,
        newPos,
      );
    },

    // Select a filter chip - if this means all will be selected, ensure all gets set to selected
    setServiceFilterChipSelected: (
      draft: Draft<TimeSeriesStoreType>,
      action: PayloadAction<{ serviceId: string; panelId?: string }>,
    ) => {
      const serviceFilterChipsById =
        draft.timeseriesSelect?.filters.serviceFilterChips.entities;
      if (serviceFilterChipsById) {
        const activeServices = compact(Object.values(serviceFilterChipsById));
        const countSelectedServices = activeServices.filter(
          (service) => service.enabled,
        ).length;
        const isAllServicesGoingToBeEnabled =
          countSelectedServices === activeServices.length - 1;
        if (isAllServicesGoingToBeEnabled) {
          panelSlice.caseReducers.setAllServiceFilterChipSelected(draft, {
            type: timeSeriesActions.setAllServiceFilterChipSelected.type,
            payload: {},
          });
          return;
        }

        const service = serviceFilterChipsById[action.payload.serviceId]!;
        service.enabled = true;
      }
    },
    // Select all filter chips
    setAllServiceFilterChipSelected: (
      draft: Draft<TimeSeriesStoreType>,
      _action: PayloadAction<{ panelId?: string }>,
    ) => {
      if (draft.timeseriesSelect) {
        const serviceFilterChipsById =
          draft.timeseriesSelect.filters.serviceFilterChips.entities;
        const servicesToTurnOn = compact(
          Object.values(serviceFilterChipsById),
        ).filter((service) => !service.enabled);

        const updates = servicesToTurnOn.map((service) => ({
          id: service.serviceId!,
          changes: { enabled: true },
        }));
        timeseriesSelectServiceFilterChipsAdapter.updateMany(
          draft.timeseriesSelect.filters.serviceFilterChips,
          updates,
        );
        draft.timeseriesSelect.filters.allServiceFilterChipsEnabled = true;
      }
    },
    // Unselect all selected filter chips so this is the only one remaining selected
    setOnlyThisServiceFilterChipSelected: (
      draft: Draft<TimeSeriesStoreType>,
      action: PayloadAction<{ serviceId: string; panelId?: string }>,
    ) => {
      if (draft.timeseriesSelect) {
        const serviceFilterChipsById =
          draft.timeseriesSelect.filters.serviceFilterChips.entities;

        const servicesToTurnOff = compact(
          Object.values(serviceFilterChipsById),
        ).filter((service) => service.serviceId !== action.payload.serviceId);

        const updates = servicesToTurnOff.map((service) => ({
          id: service.serviceId!,
          changes: { enabled: false },
        }));
        timeseriesSelectServiceFilterChipsAdapter.updateMany(
          draft.timeseriesSelect.filters.serviceFilterChips,
          updates,
        );

        const selectedService =
          serviceFilterChipsById[action.payload.serviceId];
        if (selectedService) {
          selectedService.enabled = true;
        }

        draft.timeseriesSelect.filters.allServiceFilterChipsEnabled = false;
      }
    },
    // Unselect one filter chip - if this means all would be unselected, automatically reselect ALL
    setServiceFilterChipUnselected: (
      draft: Draft<TimeSeriesStoreType>,
      action: PayloadAction<{ serviceId: string; panelId?: string }>,
    ) => {
      if (draft.timeseriesSelect) {
        const serviceFilterChipsById =
          draft.timeseriesSelect.filters.serviceFilterChips.entities;
        const countPressedServices = Object.values(
          serviceFilterChipsById,
        ).filter((service) => service?.enabled).length;
        const isAllServicesGoingToBeDisabled = countPressedServices === 1;
        if (isAllServicesGoingToBeDisabled) {
          panelSlice.caseReducers.setAllServiceFilterChipSelected(draft, {
            type: timeSeriesActions.setAllServiceFilterChipSelected.type,
            payload: {},
          });
          return;
        }

        const service = serviceFilterChipsById[action.payload.serviceId]!;
        service.enabled = false;
      }
    },
    setServiceFilterChipsInStore: (
      draft: Draft<TimeSeriesStoreType>,
      action: PayloadAction<ServiceFilterChipsObject[]>,
    ) => {
      action.payload.forEach((service) => {
        if (draft.timeseriesSelect) {
          draft.timeseriesSelect.filters.serviceFilterChips.entities[
            service.serviceId
          ] = {
            serviceId: service.serviceId,
            serviceName: service.serviceName || service.serviceId,
            enabled:
              draft.timeseriesSelect?.filters.allServiceFilterChipsEnabled,
            scope: service.scope || 'system',
            type: service.type,
            serviceUrl: service.serviceUrl,
            isLoading: false,
          };

          if (
            !draft.timeseriesSelect.filters.serviceFilterChips.ids.includes(
              service.serviceId,
            )
          ) {
            draft.timeseriesSelect.filters.serviceFilterChips.ids.push(
              service.serviceId,
            );
          }
        }
      });
    },

    removeServiceFilterChipFromStore: (
      draft: Draft<TimeSeriesStoreType>,
      action: PayloadAction<RemoveServiceFilterChipFromStorePayload>,
    ) => {
      if (draft.timeseriesSelect) {
        draft.timeseriesSelect.filters.serviceFilterChips.ids =
          draft.timeseriesSelect.filters.serviceFilterChips.ids.filter(
            (id) => id !== action.payload.id,
          );
        delete draft.timeseriesSelect.filters.serviceFilterChips.entities[
          action.payload.id
        ];
      }
    },

    setCurrentParameterInfoDisplayed: (
      draft: Draft<TimeSeriesStoreType>,
      action: PayloadAction<SetCurrentParameterInfo>,
    ) => {
      const { parameter } = action.payload;
      if (draft.timeseriesSelect) {
        draft.timeseriesSelect.currentParameterInfo = parameter;
      }
    },
    addServices: (
      draft: Draft<TimeSeriesStoreType>,
      action: PayloadAction<SetTimeSeriesServicesPayload>,
    ) => {
      const newServicesAdded: ServiceFilterChipsObject[] =
        action.payload.timeSeriesServices.map((service) => ({
          serviceId: service.id,
          serviceUrl: service.url,
          serviceName: service.name,
          type: service.type,
          scope: (service.scope as SystemScope) || 'system',
        }));
      // Add filterchips for any new services that were added
      panelSlice.caseReducers.setServiceFilterChipsInStore(draft, {
        payload: newServicesAdded,
        type: timeSeriesActions.setServiceFilterChipsInStore.type,
      });
    },
    upsertService: (
      draft: Draft<TimeSeriesStoreType>,
      action: PayloadAction<UpsertTimeSeriesServicePayload>,
    ) => {
      // Add filterchips for any new services that were added
      panelSlice.caseReducers.setServiceFilterChipsInStore(draft, {
        payload: [
          {
            serviceId: action.payload.id || generateServiceId(),
            serviceUrl: action.payload.url,
            serviceName: action.payload.name,
            type: action.payload.type,
            scope: 'user',
          },
        ],
        type: timeSeriesActions.setServiceFilterChipsInStore.type,
      });
    },
    removeService: (
      draft: Draft<TimeSeriesStoreType>,
      action: PayloadAction<RemoveTimeSeriesServicePayload>,
    ) => {
      panelSlice.caseReducers.removeServiceFilterChipFromStore(draft, {
        payload: action.payload,
        type: timeSeriesActions.removeServiceFilterChipFromStore.type,
      });
    },
    closeServicePopup: (
      draft: Draft<TimeSeriesStoreType>,
      _action: PayloadAction<{ panelId?: string }>,
    ) => {
      if (draft.timeseriesSelect) {
        draft.timeseriesSelect.servicePopup.isOpen = false;
      }
    },
    setServicePopup: (
      draft: Draft<TimeSeriesStoreType>,
      action: PayloadAction<SetTimeSeriesServicePopupPayload>,
    ) => {
      if (draft.timeseriesSelect) {
        draft.timeseriesSelect.servicePopup.isOpen = action.payload.isOpen;
        draft.timeseriesSelect.servicePopup.variant = action.payload.variant;
        draft.timeseriesSelect.servicePopup.url = action.payload.url || '';
        draft.timeseriesSelect.servicePopup.serviceId =
          action.payload.serviceId || '';
      }
    },
    disableMapPin: (
      draft: Draft<TimeSeriesStoreType>,
      action: PayloadAction<ToggleDisableMapPinPayload>,
    ) => {
      const { isMapPinDisabled } = action.payload;
      draft.locationData = {
        ...draft.locationData,
        isMapPinDisabled,
      };
    },
    toggleLocationMarkers: (
      draft: Draft<TimeSeriesStoreType>,
      action: PayloadAction<ToggleLocationMarkersPayload>,
    ) => {
      const { shouldHideLocationMarkers } = action.payload;
      draft.locationData = {
        ...draft.locationData,
        shouldHideLocationMarkers,
      };
    },
    toggleLegend: (
      draft: Draft<TimeSeriesStoreType>,
      action: PayloadAction<ToggleTimeSeriesLegendPayload>,
    ) => {
      const { isLegendVisible } = action.payload;
      draft.locationData = {
        ...draft.locationData,
        isLegendVisible,
      };
    },
    setCurrentLocation: (
      draft: Draft<TimeSeriesStoreType>,
      action: PayloadAction<SetTimeSeriesLocationPayload>,
    ) => {
      const { location } = action.payload;
      draft.locationData = {
        ...draft.locationData,
        currentLocation: location,
      };
    },
    setLocationMenuSizeAndPosition: (
      draft: Draft<TimeSeriesStoreType>,
      action: PayloadAction<SetLocationMenuSizeAndPositionPayload>,
    ) => {
      draft.locationData = {
        ...draft.locationData,
        locationMenuSize: action.payload.size,
        ...(action.payload.position !== undefined && {
          locationMenuPosition: action.payload.position,
        }),
      };
    },
    setLocationsDialogOpen: (
      draft: Draft<TimeSeriesStoreType>,
      action: PayloadAction<SetLocationsDialogOpenPayload>,
    ) => {
      draft.locationData = {
        ...draft.locationData,
        isLocationsDialogOpen: action.payload.isLocationsDialogOpen,
      };
    },
  },
});

const resetTimeSeriesPanels = createAction('timeseriesPanel/resetPanels');

export const timeSeriesActions = {
  ...panelSlice.actions,
  resetTimeSeriesPanels,
};

const resolvePanelId = (action: {
  payload?: { panelId?: string };
}): string | undefined => {
  return action.payload?.panelId;
};

const normalizeTimeSeriesState = (
  state: TimeSeriesState | TimeSeriesStoreType,
): TimeSeriesState => {
  if ('byId' in state) {
    return state;
  }
  const legacyServices =
    (state as TimeSeriesStoreType & { services?: TimeSeriesService[] })
      .services || [];
  const { services: legacyServicesValue, ...panelState } =
    state as TimeSeriesStoreType & { services?: TimeSeriesService[] };
  return {
    ...initialState,
    services: legacyServices,
    byId: {
      ...initialState.byId,
      default: panelState,
    },
  };
};

const toServiceFilterChip = (
  service: TimeSeriesService,
): ServiceFilterChipsObject => ({
  serviceId: service.id,
  serviceUrl: service.url,
  serviceName: service.name,
  type: service.type,
  scope: (service.scope as SystemScope) || 'system',
});

const addServiceFilterChipsToPanel = (
  panelState: TimeSeriesStoreType,
  servicesToAdd: TimeSeriesService[],
): TimeSeriesStoreType => {
  if (!servicesToAdd.length) {
    return panelState;
  }
  const existingChips =
    panelState.timeseriesSelect?.filters.serviceFilterChips.entities || {};
  const servicesMissingChips = servicesToAdd.filter(
    (service) => !existingChips[service.id],
  );
  if (!servicesMissingChips.length) {
    return panelState;
  }
  return panelSlice.reducer(
    panelState,
    timeSeriesActions.setServiceFilterChipsInStore(
      servicesMissingChips.map(toServiceFilterChip),
    ),
  );
};

const removeServiceFilterChipsFromPanel = (
  panelState: TimeSeriesStoreType,
  serviceIdsToRemove: string[],
): TimeSeriesStoreType => {
  if (!serviceIdsToRemove.length) {
    return panelState;
  }
  return serviceIdsToRemove.reduce(
    (nextState, id) =>
      panelSlice.reducer(
        nextState,
        timeSeriesActions.removeServiceFilterChipFromStore({ id }),
      ),
    panelState,
  );
};

export const timeSeriesReducer: Reducer<TimeSeriesState> = (
  state: TimeSeriesState | undefined,
  action: UnknownAction,
): TimeSeriesState => {
  const normalizedState = normalizeTimeSeriesState(state ?? initialState);
  const isTimeSeriesAction =
    typeof action.type === 'string' &&
    action.type.startsWith(`${panelSlice.name}/`);

  if (action.type === resetTimeSeriesPanels.type) {
    return {
      ...normalizedState,
      byId: {},
    };
  }

  if (!isTimeSeriesAction) {
    return normalizedState;
  }
  const panelId = resolvePanelId(action as { payload?: { panelId?: string } });

  const serviceActionTypes = new Set<string>([
    timeSeriesActions.loadUserAddedTimeSeriesServices.type,
    timeSeriesActions.addServices.type,
    timeSeriesActions.upsertService.type,
    timeSeriesActions.removeService.type,
    timeSeriesActions.registerTimeSeriesPreset.type,
  ]);

  let nextServices = normalizedState.services;

  if (action.type === timeSeriesActions.loadUserAddedTimeSeriesServices.type) {
    const { payload } = action as PayloadAction<
      SetTimeSeriesServicesPayload | TimeSeriesService[]
    >;
    const servicesPayload = Array.isArray(payload)
      ? payload
      : payload.timeSeriesServices;
    const newServices: TimeSeriesService[] = [];
    servicesPayload.forEach((service) => {
      const existingService = nextServices.find((el) => el.url === service.url);
      if (!existingService) {
        newServices.push({
          ...service,
          id: service.scope === 'system' ? service.id : generateServiceId(),
          scope: service.scope ?? 'user',
        });
      }
    });
    if (newServices.length) {
      nextServices = [...nextServices, ...newServices];
    }
  }

  if (action.type === timeSeriesActions.addServices.type) {
    const { timeSeriesServices } = (
      action as PayloadAction<SetTimeSeriesServicesPayload>
    ).payload;
    const filteredServices = timeSeriesServices.reduce<TimeSeriesService[]>(
      (acc, service) => {
        if (!nextServices.some((existing) => existing.url === service.url)) {
          const hasDuplicateId = nextServices.some(
            (existing) => existing.id === service.id,
          );
          if (hasDuplicateId) {
            acc.push({
              ...service,
              id: generateServiceId(),
            } as TimeSeriesService);
          } else {
            acc.push(service);
          }
        }
        return acc;
      },
      [],
    );
    if (filteredServices.length) {
      nextServices = [...nextServices, ...filteredServices];
    }
  }

  if (action.type === timeSeriesActions.upsertService.type) {
    const { payload } = action as PayloadAction<UpsertTimeSeriesServicePayload>;
    const newService: TimeSeriesService = {
      id: payload.id || generateServiceId(),
      description: payload.description,
      url: payload.url,
      name: payload.name,
      type: payload.type,
      scope: 'user',
    };
    if (payload.isUpdate) {
      const index = nextServices.findIndex((el) => el.id === payload.id);
      if (index >= 0) {
        nextServices = [
          ...nextServices.slice(0, index),
          newService,
          ...nextServices.slice(index + 1),
        ];
      }
    } else {
      nextServices = [...nextServices, newService];
    }
  }

  if (action.type === timeSeriesActions.removeService.type) {
    const { id } = (action as PayloadAction<RemoveTimeSeriesServicePayload>)
      .payload;
    nextServices = nextServices.filter((service) => service.id !== id);
  }

  const previousServices = normalizedState.services;
  const addedServices = nextServices.filter(
    (service) =>
      !previousServices.some((existing) => existing.url === service.url),
  );
  const updatedServices = nextServices.filter((service) => {
    const previous = previousServices.find(
      (existing) => existing.id === service.id,
    );
    return (
      previous &&
      (previous.name !== service.name ||
        previous.url !== service.url ||
        previous.type !== service.type ||
        previous.scope !== service.scope)
    );
  });
  const removedServiceIds = previousServices
    .filter(
      (service) => !nextServices.some((existing) => existing.id === service.id),
    )
    .map((service) => service.id);

  const servicesToSync = [
    ...addedServices,
    ...updatedServices.filter(
      (service) => !addedServices.some((added) => added.id === service.id),
    ),
  ];
  const syncServiceChipsAcrossPanels = (
    byId: Record<string, TimeSeriesStoreType>,
    overrideServicesToSync?: TimeSeriesService[],
    overrideRemovedServiceIds?: string[],
  ): Record<string, TimeSeriesStoreType> => {
    const servicesForSync = overrideServicesToSync ?? servicesToSync;
    const removedIdsForSync = overrideRemovedServiceIds ?? removedServiceIds;
    const shouldSync =
      servicesForSync.length > 0 || removedIdsForSync.length > 0;
    if (!shouldSync) {
      return byId;
    }
    const nextById: Record<string, TimeSeriesStoreType> = {};
    let didChange = false;
    Object.entries(byId).forEach(([id, panelState]) => {
      let nextPanelState = panelState;
      nextPanelState = addServiceFilterChipsToPanel(
        nextPanelState,
        servicesForSync,
      );
      nextPanelState = removeServiceFilterChipsFromPanel(
        nextPanelState,
        removedIdsForSync,
      );
      if (nextPanelState !== panelState) {
        didChange = true;
      }
      nextById[id] = nextPanelState;
    });
    return didChange ? nextById : byId;
  };

  if (action.type === timeSeriesActions.registerTimeSeriesPreset.type) {
    const { payload } =
      action as PayloadAction<RegisterTimeSeriesPresetPayload>;
    if (!panelId) {
      return {
        ...normalizedState,
        services: nextServices,
        byId: syncServiceChipsAcrossPanels(normalizedState.byId),
      };
    }
    const currentPanelState =
      normalizedState.byId[panelId] ?? createPanelState();
    const newServicesAdded: ServiceFilterChipsObject[] = [];

    const presetServices = payload.services ?? [];
    const timeSeriesPresetWithParameterIds = produce(payload, (draftPreset) => {
      draftPreset.plotPreset.parameters.forEach((draftParameter) => {
        draftParameter.id = generateParameterId();
        draftParameter.opacity = draftParameter.opacity || 70;
        draftParameter.dimensions = getNormalizedDimensions(draftParameter);
        delete (draftParameter as Parameter & { dimension?: unknown })
          .dimension;

        const serviceFromStore = getServiceById(
          nextServices,
          draftParameter.serviceId,
        );
        if (!serviceFromStore) {
          const serviceFromPreset = getServiceById(
            presetServices,
            draftParameter.serviceId,
          );
          if (!serviceFromPreset) {
            console.error(
              `A parameter from the preset has a service with id ${draftParameter.serviceId} which is not in the store or in the preset`,
            );
            return;
          }
          const reUseAbleService = nextServices.find(
            (storeService) => storeService.url === serviceFromPreset.url,
          );
          if (!reUseAbleService) {
            nextServices = [
              ...nextServices,
              { ...serviceFromPreset, scope: 'system' },
            ];
            newServicesAdded.push({
              serviceId: serviceFromPreset.id,
              serviceUrl: serviceFromPreset.url,
              serviceName: serviceFromPreset.name,
              type: serviceFromPreset.type,
              scope: 'system',
            });
          } else {
            draftParameter.serviceId = reUseAbleService.id;
            nextServices = nextServices.map((service) =>
              service.id === reUseAbleService.id
                ? { ...service, scope: 'system' }
                : service,
            );
          }
        }
      });
    });

    presetServices.forEach((serviceFromPreset) => {
      const serviceFromStore = nextServices.find(
        (service) => service.url === serviceFromPreset.url,
      );
      if (!serviceFromStore) {
        const uniqueServiceId = nextServices.some(
          (service) => service.id === serviceFromPreset.id,
        )
          ? generateServiceId()
          : serviceFromPreset.id;
        nextServices = [
          ...nextServices,
          {
            ...serviceFromPreset,
            id: uniqueServiceId,
            scope: 'system',
          },
        ];
        newServicesAdded.push({
          serviceId: uniqueServiceId,
          serviceUrl: serviceFromPreset.url,
          serviceName: serviceFromPreset.name,
          type: serviceFromPreset.type,
          scope: 'system',
        });
      } else {
        nextServices = nextServices.map((service) =>
          service.id === serviceFromStore.id
            ? { ...service, scope: 'system' }
            : service,
        );
      }
    });

    const nextPanelState = produce(currentPanelState, (draft) => {
      draft.plotPreset = timeSeriesPresetWithParameterIds.plotPreset;
      panelSlice.caseReducers.setServiceFilterChipsInStore(draft, {
        payload: newServicesAdded,
        type: timeSeriesActions.setServiceFilterChipsInStore.type,
      });
      if (!draft.locationData) {
        draft.locationData = {};
      }
      if (payload.locationMenuSize) {
        draft.locationData.locationMenuSize = payload.locationMenuSize;
      }
      if (payload.locationMenuPosition) {
        draft.locationData.locationMenuPosition = payload.locationMenuPosition;
      }
      if (payload.isLocationsDialogOpen !== undefined) {
        draft.locationData.isLocationsDialogOpen =
          payload.isLocationsDialogOpen;
      }
    });

    const updatedById = {
      ...normalizedState.byId,
      [panelId]: nextPanelState,
    };

    const servicesToSyncFromPreset = Array.from(
      new Map(
        [
          ...newServicesAdded.map((service) =>
            nextServices.find(
              (nextService) => nextService.id === service.serviceId,
            ),
          ),
          ...presetServices.map((service) =>
            nextServices.find(
              (nextService) =>
                nextService.url === service.url ||
                nextService.id === service.id,
            ),
          ),
        ]
          .filter((service): service is TimeSeriesService => !!service)
          .map((service) => [service.id, service]),
      ).values(),
    );

    return {
      ...normalizedState,
      services: nextServices,
      byId: syncServiceChipsAcrossPanels(updatedById, servicesToSyncFromPreset),
      locationDialogRestoreVersion:
        (normalizedState.locationDialogRestoreVersion ?? 0) + 1,
    };
  }

  if (!panelId) {
    if (serviceActionTypes.has(action.type)) {
      return {
        ...normalizedState,
        services: nextServices,
        byId: syncServiceChipsAcrossPanels(normalizedState.byId),
      };
    }
    return normalizedState;
  }

  const currentPanelState = normalizedState.byId[panelId];
  let basePanelState = currentPanelState ?? createPanelState();
  if (normalizedState.services.length) {
    basePanelState = addServiceFilterChipsToPanel(
      basePanelState,
      normalizedState.services,
    );
  }
  const nextPanelState = panelSlice.reducer(basePanelState, action);

  if (
    nextPanelState === currentPanelState &&
    nextServices === normalizedState.services
  ) {
    return normalizedState;
  }

  return {
    ...normalizedState,
    services: nextServices,
    byId: serviceActionTypes.has(action.type)
      ? syncServiceChipsAcrossPanels({
          ...normalizedState.byId,
          [panelId]: nextPanelState,
        })
      : {
          ...normalizedState.byId,
          [panelId]: nextPanelState,
        },
  };
};

const generateTimeSeriesId = (): string => {
  return `timeseriesid_${uuidv4()}`;
};

const generateParameterId = (): string => {
  return `timeseriesparamid_${uuidv4()}`;
};

export const generateServiceId = (): string => {
  return `timeseriesserviceid_${uuidv4()}`;
};
